import ExcelJS from 'exceljs';
import { jsPDF } from 'jspdf';
import type { Candidate } from '@core/types';
import {
  CANDIDATE_FULL_EXPORT_COLUMN_ORDER,
  candidateFullFlatForExport,
} from './candidatesListPreferences';

const DEFAULT_BRAND = 'GoGrabJob ATS';
const LOGO_PATH = '/ggj-mark-ref.png';

/** 1-based column index → Excel column letters (A, B, …, Z, AA, …). */
function excelColLetter(colIndex1Based: number): string {
  let n = colIndex1Based;
  let s = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s || 'A';
}

export type CandidateExportMeta = {
  brandTitle: string;
  reportTitle: string;
  subtitle: string;
  generatedAt: Date;
  recordCount: number;
};

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** PNG base64 without data URL prefix (for ExcelJS). */
async function tryLoadBrandLogoBase64Png(): Promise<string | null> {
  try {
    const res = await fetch(new URL(LOGO_PATH, window.location.origin).href);
    if (!res.ok) return null;
    const buf = await res.arrayBuffer();
    let binary = '';
    const bytes = new Uint8Array(buf);
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]!);
    return btoa(binary);
  } catch {
    return null;
  }
}

/** Data URL for jsPDF.addImage */
async function tryLoadBrandLogoDataUrl(): Promise<string | null> {
  const raw = await tryLoadBrandLogoBase64Png();
  if (!raw) return null;
  return `data:image/png;base64,${raw}`;
}

function cellString(v: string | number | boolean): string {
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v ?? '');
}

export function exportCandidatesCsv(rows: Candidate[], filename: string) {
  const keys = CANDIDATE_FULL_EXPORT_COLUMN_ORDER;
  const header = keys.join(',');
  const lines = rows.map((c) => {
    const o = candidateFullFlatForExport(c);
    return keys.map((k) => `"${cellString(o[k]).replace(/"/g, '""')}"`).join(',');
  });
  const blob = new Blob([['\ufeff', header, ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
  triggerDownload(blob, filename.endsWith('.csv') ? filename : `${filename}.csv`);
}

export async function exportCandidatesXlsx(rows: Candidate[], filename: string, meta: CandidateExportMeta) {
  const keys = CANDIDATE_FULL_EXPORT_COLUMN_ORDER;
  const wb = new ExcelJS.Workbook();
  wb.creator = meta.brandTitle;
  wb.created = meta.generatedAt;
  const ws = wb.addWorksheet('Candidates', {
    views: [{ state: 'frozen', ySplit: 6 }],
  });

  const lastCol = keys.length;
  const lastLetter = excelColLetter(lastCol);
  const logoB64 = await tryLoadBrandLogoBase64Png();
  if (logoB64) {
    const imageId = wb.addImage({ base64: logoB64, extension: 'png' });
    ws.addImage(imageId, {
      tl: { col: 0, row: 0 },
      ext: { width: 52, height: 52 },
    });
  }

  const titleStart = logoB64 ? 'B' : 'A';
  ws.mergeCells(`${titleStart}1:${lastLetter}1`);
  ws.getCell(`${titleStart}1`).value = meta.brandTitle;
  ws.getCell(`${titleStart}1`).font = { size: 18, bold: true, color: { argb: 'FF4C1D95' } };

  ws.mergeCells(`${titleStart}2:${lastLetter}2`);
  ws.getCell(`${titleStart}2`).value = meta.reportTitle;
  ws.getCell(`${titleStart}2`).font = { size: 14, bold: true };

  ws.mergeCells(`${titleStart}3:${lastLetter}3`);
  ws.getCell(`${titleStart}3`).value = `${meta.subtitle} · Generated ${meta.generatedAt.toLocaleString()} · ${meta.recordCount} record(s)`;
  ws.getCell(`${titleStart}3`).font = { size: 10, color: { argb: 'FF52525B' } };

  ws.getRow(1).height = logoB64 ? 42 : 28;
  ws.getRow(2).height = 22;
  ws.getRow(3).height = 20;
  ws.getRow(4).height = 8;

  const headerRowIndex = 5;
  keys.forEach((key, i) => {
    const cell = ws.getCell(headerRowIndex, i + 1);
    cell.value = key;
    cell.font = { bold: true };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF4F4F5' },
    };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFE4E4E7' } },
      left: { style: 'thin', color: { argb: 'FFE4E4E7' } },
      bottom: { style: 'thin', color: { argb: 'FFE4E4E7' } },
      right: { style: 'thin', color: { argb: 'FFE4E4E7' } },
    };
  });

  rows.forEach((row, ri) => {
    const flat = candidateFullFlatForExport(row);
    keys.forEach((key, ci) => {
      const cell = ws.getCell(headerRowIndex + 1 + ri, ci + 1);
      const v = flat[key];
      cell.value = typeof v === 'boolean' ? (v ? 'true' : 'false') : v === '' ? '' : v;
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFF4F4F5' } },
        left: { style: 'thin', color: { argb: 'FFE4E4E7' } },
        bottom: { style: 'thin', color: { argb: 'FFE4E4E7' } },
        right: { style: 'thin', color: { argb: 'FFE4E4E7' } },
      };
    });
  });

  ws.columns = keys.map((k) => ({
    width: Math.min(52, Math.max(10, String(k).length * 0.75 + 10)),
  }));

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  triggerDownload(blob, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
}

function drawBrandedPdfHeader(
  doc: jsPDF,
  meta: CandidateExportMeta,
  logoDataUrl: string | null,
  margin: number,
  contentWidth: number
): number {
  let y = margin;
  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, 'PNG', margin, y, 40, 40);
    } catch {
      /* ignore bad image */
    }
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(76, 29, 149);
  doc.text(meta.brandTitle, logoDataUrl ? margin + 48 : margin, y + 14);
  doc.setFontSize(12);
  doc.setTextColor(24, 24, 27);
  doc.text(meta.reportTitle, logoDataUrl ? margin + 48 : margin, y + 30);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(82, 82, 91);
  const sub = `${meta.subtitle} · ${meta.generatedAt.toLocaleString()} · ${meta.recordCount} record(s)`;
  const subLines = doc.splitTextToSize(sub, contentWidth - (logoDataUrl ? 48 : 0));
  doc.text(subLines, logoDataUrl ? margin + 48 : margin, y + 42);
  y += logoDataUrl ? 52 : 40;
  doc.setDrawColor(228, 228, 231);
  doc.line(margin, y, margin + contentWidth, y);
  y += 16;
  return y;
}

function exportCandidatesPdfSingle(doc: jsPDF, c: Candidate, meta: CandidateExportMeta, logoDataUrl: string | null) {
  const margin = 48;
  const pageW = doc.internal.pageSize.getWidth();
  const contentWidth = pageW - margin * 2;
  let y = drawBrandedPdfHeader(doc, meta, logoDataUrl, margin, contentWidth);

  const flat = candidateFullFlatForExport(c);
  const keys = CANDIDATE_FULL_EXPORT_COLUMN_ORDER;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(24, 24, 27);
  doc.text(c.full_name || 'Candidate', margin, y);
  y += 18;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const lineH = 11;
  const labelW = 118;
  const pageMax = doc.internal.pageSize.getHeight() - margin;

  keys.forEach((key) => {
    const val = cellString(flat[key]);
    if (y + lineH > pageMax) {
      doc.addPage();
      y = margin;
    }
    doc.setFont('helvetica', 'bold');
    doc.text(String(key), margin, y);
    doc.setFont('helvetica', 'normal');
    const valueLines = doc.splitTextToSize(val, contentWidth - labelW);
    doc.text(valueLines, margin + labelW, y);
    y += valueLines.length * lineH + 5;
  });
}

const PDF_COLS: { key: keyof ReturnType<typeof candidateFullFlatForExport>; label: string; w: number }[] = [
  { key: 'full_name', label: 'Name', w: 0.13 },
  { key: 'email', label: 'Email', w: 0.2 },
  { key: 'phone', label: 'Phone', w: 0.09 },
  { key: 'current_title', label: 'Title', w: 0.14 },
  { key: 'current_company', label: 'Company', w: 0.12 },
  { key: 'location', label: 'Location', w: 0.1 },
  { key: 'source', label: 'Source', w: 0.08 },
  { key: 'created_at', label: 'Created', w: 0.14 },
];

function exportCandidatesPdfMulti(
  doc: jsPDF,
  rows: Candidate[],
  meta: CandidateExportMeta,
  logoDataUrl: string | null
) {
  const margin = 40;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentWidth = pageW - margin * 2;
  let y = drawBrandedPdfHeader(doc, meta, logoDataUrl, margin, contentWidth);

  const colWidths = PDF_COLS.map((c) => c.w * contentWidth);
  const lineH = 9;
  const pageMax = pageH - margin;

  y += 8;
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(24, 24, 27);
  let x = margin;
  PDF_COLS.forEach((col, i) => {
    const lines = doc.splitTextToSize(col.label, colWidths[i]! - 4);
    doc.text(lines, x + 2, y);
    x += colWidths[i]!;
  });
  y += 14;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(39, 39, 42);

  rows.forEach((c) => {
    const flat = candidateFullFlatForExport(c);
    const cells = PDF_COLS.map((col, i) => doc.splitTextToSize(cellString(flat[col.key]), colWidths[i]! - 4));
    const rowH = Math.max(1, ...cells.map((l) => l.length)) * lineH + 8;
    if (y + rowH > pageMax) {
      doc.addPage();
      y = margin + 12;
      doc.setFontSize(8);
      doc.setTextColor(82, 82, 91);
      doc.text(`Candidates (continued) · ${meta.recordCount} total`, margin, y);
      y += 22;
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(24, 24, 27);
      let hx = margin;
      PDF_COLS.forEach((col, i) => {
        const lines = doc.splitTextToSize(col.label, colWidths[i]! - 4);
        doc.text(lines, hx + 2, y);
        hx += colWidths[i]!;
      });
      y += 14;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(39, 39, 42);
    }
    let cx = margin;
    doc.setFontSize(7);
    cells.forEach((lines, i) => {
      doc.text(lines, cx + 2, y);
      cx += colWidths[i]!;
    });
    y += rowH;
    doc.setDrawColor(228, 228, 231);
    doc.line(margin, y - 4, margin + contentWidth, y - 4);
  });

  if (y + 20 < pageMax) {
    doc.setFontSize(8);
    doc.setTextColor(82, 82, 91);
    doc.text(
      'Summary columns only. Use Excel export for the full candidate object (all fields).',
      margin,
      y + 10
    );
  }
}

export async function exportCandidatesPdf(rows: Candidate[], filename: string, meta: CandidateExportMeta) {
  const logoDataUrl = await tryLoadBrandLogoDataUrl();
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

  if (rows.length === 1) {
    exportCandidatesPdfSingle(doc, rows[0]!, meta, logoDataUrl);
  } else {
    exportCandidatesPdfMulti(doc, rows, meta, logoDataUrl);
  }

  doc.save(filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
}

export function defaultExportMeta(
  partial: Partial<CandidateExportMeta> & Pick<CandidateExportMeta, 'recordCount'>
): CandidateExportMeta {
  return {
    brandTitle: partial.brandTitle ?? (import.meta.env.VITE_APP_TITLE || DEFAULT_BRAND),
    reportTitle: partial.reportTitle ?? 'Candidates',
    subtitle: partial.subtitle ?? 'Export',
    generatedAt: partial.generatedAt ?? new Date(),
    recordCount: partial.recordCount,
  };
}
